(() => {
  const number = (value) => Number.parseFloat(String(value || '').replace(/[^\d.]/g, '')) || 0;

  const setupPriceRange = (slider) => {
    if (slider.dataset.rdReady === 'true') return;

    const group = slider.closest('price-range');
    const minField = group?.querySelector('[data-rd-price-min]');
    const maxField = group?.querySelector('[data-rd-price-max]');
    const minRange = slider.querySelector('[data-rd-range-min]');
    const maxRange = slider.querySelector('[data-rd-range-max]');
    if (!group || !minField || !maxField || !minRange || !maxRange) return;

    slider.dataset.rdReady = 'true';
    const limit = number(maxRange.max) || 1;

    const paint = () => {
      slider.style.setProperty('--rd-price-start', `${number(minRange.value) / limit * 100}%`);
      slider.style.setProperty('--rd-price-end', `${number(maxRange.value) / limit * 100}%`);
    };

    const syncFromSliders = (source) => {
      let min = number(minRange.value);
      let max = number(maxRange.value);
      if (min > max) source === minRange ? min = max : max = min;
      minRange.value = min;
      maxRange.value = max;
      minField.value = min > 0 ? Math.round(min) : '';
      maxField.value = max < limit ? Math.round(max) : '';
      paint();
    };

    const syncFromFields = () => {
      const min = Math.min(number(minField.value), limit);
      const max = maxField.value.trim() ? Math.min(number(maxField.value), limit) : limit;
      minRange.value = Math.min(min, max);
      maxRange.value = Math.max(min, max);
      paint();
    };

    [minRange, maxRange].forEach((range) => {
      range.addEventListener('input', () => syncFromSliders(range));
      range.addEventListener('change', () => {
        syncFromSliders(range);
        maxField.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    [minField, maxField].forEach((field) => field.addEventListener('input', syncFromFields));
    syncFromFields();
  };

  const init = (root = document) => root.querySelectorAll('[data-rd-price-slider]').forEach(setupPriceRange);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => init()) : init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  new MutationObserver((changes) => {
    if (changes.some((change) => change.addedNodes.length)) init();
  }).observe(document.documentElement, { childList: true, subtree: true });

  const normalise = (value) => String(value || '').trim().toLocaleLowerCase();
  const selected = { concerns: new Set(), category: new Set() };
  const customUrlParameters = {
    concerns: 'concern',
    category: 'category',
  };
  const customUrlAliases = {
    concerns: ['concern', 'concerns', 'rd_concern'],
    category: ['category', 'rd_category'],
  };

  const addParameterValues = (target, parameters, names) => {
    names.forEach((name) => {
      parameters.getAll(name).forEach((value) => {
        const cleanValue = normalise(value);
        if (cleanValue) target.add(cleanValue);
      });
    });
  };

  const readCustomFiltersFromUrl = () => {
    const parameters = new URLSearchParams(window.location.search);
    selected.concerns.clear();
    selected.category.clear();
    addParameterValues(selected.concerns, parameters, customUrlAliases.concerns);
    addParameterValues(selected.category, parameters, customUrlAliases.category);
  };

  const addCustomFiltersToParameters = (parameters) => {
    Object.values(customUrlAliases).flat().forEach((name) => parameters.delete(name));

    Object.entries(customUrlParameters).forEach(([type, name]) => {
      [...selected[type]].sort().forEach((value) => parameters.append(name, value));
    });

    return parameters;
  };

  const customUrl = (destination = window.location.href) => {
    const url = new URL(destination, window.location.origin);
    addCustomFiltersToParameters(url.searchParams);
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const syncCustomFormInputs = () => {
    const forms = [
      document.getElementById('FacetFiltersForm'),
      document.getElementById('FacetFiltersFormMobile'),
    ].filter(Boolean);

    forms.forEach((form) => {
      const desired = [];
      Object.entries(customUrlParameters).forEach(([type, name]) => {
        [...selected[type]].sort().forEach((value) => {
          desired.push(`${name}=${value}`);
        });
      });

      const currentInputs = [...form.querySelectorAll('[data-rd-custom-url-input]')];
      const current = currentInputs.map((input) => `${input.name}=${input.value}`);
      if (current.length === desired.length && current.every((value, index) => value === desired[index])) return;

      currentInputs.forEach((input) => input.remove());
      Object.entries(customUrlParameters).forEach(([type, name]) => {
        [...selected[type]].sort().forEach((value) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          input.dataset.rdCustomUrlInput = 'true';
          form.appendChild(input);
        });
      });
    });
  };

  const updateCustomUrl = () => {
    const parameters = new URLSearchParams(window.location.search);
    parameters.delete('page');
    addCustomFiltersToParameters(parameters);
    const search = parameters.toString();
    const nextUrl = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
    history.pushState({ searchParams: search }, '', nextUrl);
    syncCustomFormInputs();
  };

  readCustomFiltersFromUrl();

  const closeMobileSubmenu = (details) => {
    if (!details) return;
    const closeButton = details.querySelector('.mobile-facets__close-button');
    if (closeButton) {
      closeButton.click();
      return;
    }
    details.removeAttribute('open');
    details.classList.remove('menu-opening');
    details.closest('.mobile-facets__main')?.classList.remove('submenu-open');
  };

  const closeMobileDrawer = (wrapper) => {
    const disclosure = wrapper?.querySelector('.mobile-facets__disclosure');
    const trigger = disclosure?.querySelector(':scope > .mobile-facets__open-wrapper');
    if (disclosure?.hasAttribute('open') && trigger) trigger.click();
  };

  const clearNativeMobileFilter = (details) => {
    const form = details?.closest('form');
    if (!details || !form) return;

    details.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = false;
    });
    details.querySelectorAll('input[type="number"], input[type="text"]').forEach((input) => {
      input.value = '';
    });
    details.querySelectorAll('[data-rd-range-min]').forEach((input) => {
      input.value = input.min || 0;
    });
    details.querySelectorAll('[data-rd-range-max]').forEach((input) => {
      input.value = input.max || 0;
    });

    form.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const updateCustomCounts = () => {
    document.querySelectorAll('[data-rd-custom-filter]').forEach((filter) => {
      const type = filter.dataset.rdCustomFilter;
      const count = selected[type]?.size || 0;
      const label = filter.querySelector('[data-rd-selected-count]');
      if (label) {
        const template = count === 1 ? label.dataset.rdSelectedOne : label.dataset.rdSelectedOther;
        label.textContent = template ? template.replace('__COUNT__', String(count)) : String(count);
      }
    });
  };

  const syncCustomOptions = () => {
    document.querySelectorAll('[data-rd-custom-option]').forEach((option) => {
      const type = option.dataset.rdCustomOption;
      option.checked = selected[type]?.has(normalise(option.value)) || false;
    });
  };

  const applyCustomFilters = () => {
    const cards = document.querySelectorAll('#product-grid > .grid__item[data-rd-concerns]');
    let visible = 0;

    cards.forEach((card) => {
      const concerns = new Set(String(card.dataset.rdConcerns || '').split('|||').map(normalise).filter(Boolean));
      const categories = new Set(String(card.dataset.rdCategory || '').split('|||').map(normalise).filter(Boolean));
      const concernMatch = selected.concerns.size === 0 || [...selected.concerns].some((value) => concerns.has(value));
      const categoryMatch = selected.category.size === 0 || [...selected.category].some((value) => categories.has(value));
      const show = concernMatch && categoryMatch;
      card.hidden = !show;
      if (show) visible += 1;
    });

    const grid = document.querySelector('#product-grid');
    const emptyMessage = grid?.querySelector('[data-rd-custom-empty]');
    if (grid && cards.length && visible === 0 && !emptyMessage) {
      const message = document.createElement('li');
      message.dataset.rdCustomEmpty = 'true';
      message.className = 'rd-custom-filter-empty';
      message.textContent = 'No products match the selected filters.';
      grid.appendChild(message);
    } else if (visible > 0 && emptyMessage) {
      emptyMessage.remove();
    }

    syncCustomOptions();
    updateCustomCounts();
    syncCustomFormInputs();
  };

  document.addEventListener('input', (event) => {
    if (event.target.closest('[data-rd-custom-option]')) event.stopPropagation();
  }, true);

  document.addEventListener('change', (event) => {
    const option = event.target.closest('[data-rd-custom-option]');
    if (!option) return;
    const type = option.dataset.rdCustomOption;
    const value = normalise(option.value);
    option.checked ? selected[type].add(value) : selected[type].delete(value);
    updateCustomUrl();
    applyCustomFilters();
  });

  document.addEventListener('click', (event) => {
    const reset = event.target.closest('[data-rd-custom-reset]');
    if (!reset) return;
    event.preventDefault();
    const filter = reset.closest('[data-rd-custom-filter]');
    const type = filter?.dataset.rdCustomFilter;
    if (!type) return;
    selected[type].clear();
    document.querySelectorAll(`[data-rd-custom-option="${type}"]`).forEach((option) => { option.checked = false; });
    updateCustomUrl();
    applyCustomFilters();
  });

  document.addEventListener('click', (event) => {
    const nativeRemove = event.target.closest('facet-remove a');
    if (nativeRemove && !nativeRemove.matches('[data-rd-mobile-clear-all], .active-facets__button-remove')) {
      nativeRemove.setAttribute('href', customUrl(nativeRemove.href));
    }

    if (nativeRemove?.matches('.active-facets__button-remove')) {
      selected.concerns.clear();
      selected.category.clear();
    }

    const clearAll = event.target.closest('[data-rd-mobile-clear-all]');
    if (clearAll) {
      event.preventDefault();
      event.stopImmediatePropagation();
      selected.concerns.clear();
      selected.category.clear();
      const destination = clearAll.getAttribute('href') || window.location.pathname;
      window.location.assign(destination);
      return;
    }

    const clearFilter = event.target.closest('[data-rd-mobile-clear-filter]');
    if (clearFilter) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const details = clearFilter.closest('.mobile-facets__details');
      clearNativeMobileFilter(details);
      closeMobileSubmenu(details);
      return;
    }

    const submenuApply = event.target.closest('[data-rd-mobile-submenu-apply]');
    if (submenuApply) {
      event.preventDefault();
      closeMobileSubmenu(submenuApply.closest('.mobile-facets__details'));
      return;
    }

    const apply = event.target.closest('[data-rd-mobile-apply]');
    if (apply) {
      event.preventDefault();
      closeMobileDrawer(apply.closest('.mobile-facets__wrapper'));
    }
  }, true);

  const customObserver = new MutationObserver((changes) => {
    if (!changes.some((change) => change.addedNodes.length)) return;
    window.requestAnimationFrame(() => {
      syncCustomOptions();
      syncCustomFormInputs();
      if (selected.concerns.size || selected.category.size) applyCustomFilters();
    });
  });
  customObserver.observe(document.documentElement, { childList: true, subtree: true });

  const initialiseCustomFilters = () => {
    syncCustomOptions();
    syncCustomFormInputs();
    applyCustomFilters();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseCustomFilters, { once: true });
  } else {
    initialiseCustomFilters();
  }

  window.addEventListener('popstate', () => {
    readCustomFiltersFromUrl();
    window.requestAnimationFrame(initialiseCustomFilters);
  });
})();
